import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { login, register } from 'data/api/authApi';
import { TOKEN_KEYS } from 'data/constants/tokenConstants';
import {
  AuthErrorCode,
  resolveLoginErrorCode,
  resolveSignUpErrorCode,
} from 'helpers';
import {
  fetchProfile,
  fetchAddress,
  fetchBankInfo,
  fetchPersonalInfo,
} from './profileSlice';

const savedAuth = localStorage.getItem('isAuthenticated');
const initialAuthState = savedAuth ? JSON.parse(savedAuth) : false;

export interface User {
  id: number;
  email: string;
  role: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loginLoading: boolean;
  registerLoading: boolean;
  error: AuthErrorCode | null;
}

interface LoginPayload {
  email: string;
  password: string;
}

interface RegisterPayload {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: initialAuthState,
  loginLoading: false,
  registerLoading: false,
  error: null,
};

export const loginUser = createAsyncThunk<
  { user: User },
  LoginPayload,
  { rejectValue: AuthErrorCode }
>(
  'auth/login',
  async ({ email, password }: LoginPayload, { rejectWithValue, dispatch }) => {
    try {
      const response = await login(email, password);

      localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, response.data.token);
      localStorage.setItem('isAuthenticated', JSON.stringify(true));

      const user: User = {
        id: response.data.userData.id,
        email: response.data.userData.email,
        role: response.data.userData.role,
      };

      await Promise.all([
        dispatch(fetchProfile()),
        dispatch(fetchAddress()),
        dispatch(fetchBankInfo()),
        dispatch(fetchPersonalInfo()),
      ]);

      return { user };
    } catch (error: any) {
      return rejectWithValue(resolveLoginErrorCode(error));
    }
  },
);

export const registerUser = createAsyncThunk<
  { user: User },
  RegisterPayload,
  { rejectValue: AuthErrorCode }
>(
  'auth/register',
  async (
    { email, password, first_name, last_name }: RegisterPayload,
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await register(email, password, first_name, last_name);

      localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, response.data.token);
      localStorage.setItem('isAuthenticated', JSON.stringify(true));

      const user: User = {
        id: response.data.userData.id,
        email: response.data.userData.email,
        role: response.data.userData.role,
      };

      await Promise.all([
        dispatch(fetchProfile()),
        dispatch(fetchAddress()),
        dispatch(fetchBankInfo()),
        dispatch(fetchPersonalInfo()),
      ]);

      return { user };
    } catch (error: any) {
      return rejectWithValue(resolveSignUpErrorCode(error));
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthError: (state) => {
      state.error = null;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
      localStorage.removeItem('isAuthenticated');
	  localStorage.removeItem('user');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loginLoading = true;
        state.error = null;
      })
      .addCase(
        loginUser.fulfilled,
        (state, action: PayloadAction<{ user: User }>) => {
          state.loginLoading = false;
          state.isAuthenticated = true;
          state.user = action.payload.user;
		  localStorage.setItem('user', JSON.stringify(state.user));
          state.error = null;
        },
      )
      .addCase(
        loginUser.rejected,
        (state, action: PayloadAction<AuthErrorCode | undefined>) => {
          state.loginLoading = false;
          state.error = action.payload ?? 'unknown';
        },
      )
      .addCase(registerUser.pending, (state) => {
        state.registerLoading = true;
        state.error = null;
      })
      .addCase(
        registerUser.fulfilled,
        (state, action: PayloadAction<{ user: User }>) => {
          state.registerLoading = false;
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.error = null;
        },
      )
      .addCase(
        registerUser.rejected,
        (state, action: PayloadAction<AuthErrorCode | undefined>) => {
          state.registerLoading = false;
          state.error = action.payload ?? 'unknown';
        },
      );
  },
});

export const { clearAuthError, logout } = authSlice.actions;
export const authReducer = authSlice.reducer;

export const getAuth = (state: { auth: AuthState }) => ({
  token: localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN) || null,
  isAuthenticated: state.auth.isAuthenticated,
  user: state.auth.user,
});
